import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Trophy, Search, TrendingUp, TrendingDown } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [timePeriod, setTimePeriod] = useState("all");

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard');
    }
  };

  const filteredLeaderboard = leaderboard.filter(entry =>
    entry.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Trophy className="w-16 h-16 text-[#FFC107] mx-auto mb-4" />
            <h1 className="text-4xl font-bold mb-2">Leaderboard</h1>
            <p className="text-[#A1A1AA]">Top performing traders using AlphaBot</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
              <Input
                data-testid="search-username"
                placeholder="Search by username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#1C1C1E] border-[#27272A] text-white pl-10"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide">
              <Button
                variant={timePeriod === "week" ? "default" : "outline"}
                onClick={() => setTimePeriod("week")}
                className={`shrink-0 ${timePeriod === "week" ? "bg-[#00E676] text-black" : "border-[#27272A] text-white"}`}
              >
                This Week
              </Button>
              <Button
                variant={timePeriod === "month" ? "default" : "outline"}
                onClick={() => setTimePeriod("month")}
                className={`shrink-0 ${timePeriod === "month" ? "bg-[#00E676] text-black" : "border-[#27272A] text-white"}`}
              >
                Month
              </Button>
              <Button
                variant={timePeriod === "all" ? "default" : "outline"}
                onClick={() => setTimePeriod("all")}
                className={`shrink-0 ${timePeriod === "all" ? "bg-[#00E676] text-black" : "border-[#27272A] text-white"}`}
              >
                All Time
              </Button>
            </div>
          </div>

          {/* Leaderboard - Mobile Cards */}
          <div className="md:hidden space-y-3" data-testid="leaderboard-table">
            {filteredLeaderboard.map((entry) => (
              <div key={entry.rank} className={`bg-[#111111] border border-[#27272A] rounded-xl p-4 ${
                entry.rank <= 3 ? 'border-[#FFC107]/30' : ''
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {entry.rank === 1 && <span className="text-xl">🥇</span>}
                    {entry.rank === 2 && <span className="text-xl">🥈</span>}
                    {entry.rank === 3 && <span className="text-xl">🥉</span>}
                    {entry.rank > 3 && <span className="text-sm font-bold font-data text-[#A1A1AA]">#{entry.rank}</span>}
                    <span className="font-semibold">{entry.username}</span>
                  </div>
                  <span className="text-sm">{entry.badge}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-[10px] text-[#A1A1AA] uppercase">Profit</p>
                    <p className="font-data font-bold text-[#00E676] text-sm">${entry.total_profit.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#A1A1AA] uppercase">Win Rate</p>
                    <p className="font-data font-bold text-sm">{entry.win_rate}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#A1A1AA] uppercase">Trades</p>
                    <p className="font-data font-bold text-sm">{entry.trades_count}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Leaderboard - Desktop Table */}
          <div className="hidden md:block bg-[#111111] border border-[#27272A] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#161616] border-b border-[#27272A]">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-[#A1A1AA]">Rank</th>
                  <th className="text-left p-4 text-sm font-medium text-[#A1A1AA]">Username</th>
                  <th className="text-right p-4 text-sm font-medium text-[#A1A1AA]">Total Profit</th>
                  <th className="text-right p-4 text-sm font-medium text-[#A1A1AA]">Win Rate</th>
                  <th className="text-right p-4 text-sm font-medium text-[#A1A1AA]">Trades</th>
                  <th className="text-left p-4 text-sm font-medium text-[#A1A1AA]">Badge</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeaderboard.map((entry) => (
                  <tr key={entry.rank} className={`border-b border-[#27272A] hover:bg-[#161616] transition-colors ${
                    entry.rank <= 3 ? 'bg-[#FFC107]/5' : ''
                  }`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {entry.rank === 1 && <span className="text-2xl">🥇</span>}
                        {entry.rank === 2 && <span className="text-2xl">🥈</span>}
                        {entry.rank === 3 && <span className="text-2xl">🥉</span>}
                        <span className="font-bold font-data">{entry.rank}</span>
                      </div>
                    </td>
                    <td className="p-4 font-medium">{entry.username}</td>
                    <td className="p-4 text-right font-data font-bold text-[#00E676]">
                      ${entry.total_profit.toLocaleString()}
                    </td>
                    <td className="p-4 text-right font-data">{entry.win_rate}%</td>
                    <td className="p-4 text-right font-data">{entry.trades_count}</td>
                    <td className="p-4">
                      <span className="text-sm">{entry.badge}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
